import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight } from "@phosphor-icons/react";
import { GoogleSignInButton, AuthDivider } from "@/components/GoogleSignInButton";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u.role === "admin" ? "/command" : "/portal");
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="relative hidden lg:block">
        <img
          src="https://images.pexels.com/photos/7108700/pexels-photo-7108700.jpeg"
          alt=""
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-zinc-950/40" />
        <div className="absolute bottom-12 left-12 right-12">
          <img src="/logo.png" alt="DAFARGOS HOLDINGS LLC" className="h-20 w-20 object-contain mx-auto mb-4 rounded-xl" />
          <div className="font-display text-2xl tracking-tighter mb-2">DAFARGOS HOLDINGS LLC</div>
          <p className="text-zinc-300 max-w-sm font-serif text-2xl leading-snug">
            Your partner in business formation, funding, and management.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="overline text-primary">← Back home</Link>
          <h1 className="font-serif text-4xl font-medium mt-6 mb-2">Welcome back</h1>
          <p className="text-muted-foreground mb-8">Sign in to continue your journey.</p>

          <form onSubmit={submit} className="space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} required
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 bg-zinc-900/60 border-white/10"
                data-testid="login-email-input" placeholder="you@company.com" />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} required
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 bg-zinc-900/60 border-white/10"
                data-testid="login-password-input" placeholder="••••••••" />
            </div>
            {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full bg-primary text-primary-foreground hover:bg-[#DBC197] active:scale-95 transition-transform">
              {loading ? "Signing in..." : <>Sign in <ArrowRight size={18} className="ml-2" /></>}
            </Button>
          </form>

          <AuthDivider />
          <GoogleSignInButton label="Continue with Google" />

          <p className="mt-6 text-sm text-muted-foreground">
            New here?{" "}
            <Link to="/register" className="text-primary hover:underline" data-testid="go-register-link">
              Create an account
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
